import {
  DialogContent,
  Dialog,
  Button,
  Container,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  TextField,
  Divider,
  Link,
  Card,
  CardContent,
  Chip,
  ThemeProvider,
} from '@mui/material';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  getRedirectResult,
  type User,
} from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Trans, useTranslation } from 'react-i18next';

// Helper to track failed login attempts locally (avoids Cloud Function call for clean logins)
const FAILED_ATTEMPTS_KEY = 'signin_failed_attempts';
const hasRecentFailedAttempt = (email: string): boolean => {
  try {
    const data = localStorage.getItem(FAILED_ATTEMPTS_KEY);
    if (!data) return false;
    const attempts = JSON.parse(data);
    const entry = attempts[email.toLowerCase()];
    if (!entry) return false;
    // Check if failure was within last hour
    return Date.now() - entry.timestamp < 60 * 60 * 1000;
  } catch {
    return false;
  }
};
const recordLocalFailedAttempt = (email: string) => {
  try {
    const data = localStorage.getItem(FAILED_ATTEMPTS_KEY);
    const attempts = data ? JSON.parse(data) : {};
    attempts[email.toLowerCase()] = { timestamp: Date.now() };
    localStorage.setItem(FAILED_ATTEMPTS_KEY, JSON.stringify(attempts));
  } catch {}
};
const clearLocalFailedAttempts = (email: string) => {
  try {
    const data = localStorage.getItem(FAILED_ATTEMPTS_KEY);
    if (!data) return;
    const attempts = JSON.parse(data);
    delete attempts[email.toLowerCase()];
    localStorage.setItem(FAILED_ATTEMPTS_KEY, JSON.stringify(attempts));
  } catch {}
};

import { auth, googleProvider, db, functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';
import { createUserDocIfMissing } from '@/utils/createUserDoc';
import {
  lookupSignInMethod,
  signInWithDistrict,
  SsoNotEnabledError,
} from '@/services/ssoService';
import { useUser } from '@/contexts/UserContextNew';
import OnboardingDialog from '@/components/OnboardingDialog';
import { userPath } from '@/utils/dbPaths';

import GoogleIcon from '@mui/icons-material/Google';
import SchoolIcon from '@mui/icons-material/School';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { colors } from '@/theme/variables';
import athletoLogo from '@/assets/athleto_new.png';
import { createAppTheme } from '@/theme/theme';
import LanguageSwitcher from '../components/LanguageSwitcher';

function Login() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: userLoading } = useUser();
  const lightTheme = createAppTheme('light');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false); // Prevent redirect during auth flow

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Onboarding and verification dialogs
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);
  const [newUser, setNewUser] = useState<any>(null);

  // Forgot password flow
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  // School (district SSO) sign-in state
  const [schoolMode, setSchoolMode] = useState(false);
  const [schoolEmail, setSchoolEmail] = useState('');
  const [schoolNotice, setSchoolNotice] = useState<string | null>(null);

  // Lockout state
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState<string | null>(null);
  const [requiresPasswordReset, setRequiresPasswordReset] = useState(false);

  // Check if coming from an invitation
  const inviteCode = location.state?.inviteCode;
  const shouldSignUp = location.state?.isSignUp;

  useEffect(() => {
    if (shouldSignUp && !isSignUp) {
      setIsSignUp(true);
    }
  }, [shouldSignUp]);

  useEffect(() => {
    // Don't redirect if:
    // - Still loading user state
    // - Currently in the middle of authentication flow
    // - Showing onboarding or email verification dialogs
    if (!userLoading && user && !isAuthenticating && !showOnboarding && !showEmailVerification) {
      // Add a small delay to prevent race conditions
      const timer = setTimeout(() => {
        if (inviteCode) {
          navigate(`/join/${inviteCode}`);
        } else {
          navigate('/');
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, userLoading, navigate, inviteCode, isAuthenticating, showOnboarding, showEmailVerification]);

  /**
   * Shared post-auth sequence: wait for the onUserCreate Cloud Function to
   * provision docs/claims, verify them, then route to onboarding or the app.
   * Used by the Google popup, school SSO popup, and SSO redirect-return flows.
   */
  const completePostAuth = async (authedUser: User) => {
    // Wait for Cloud Function to create user documents
    // (onUserCreate function creates global user + profile + sets claims)
    const documentsCreated = await createUserDocIfMissing(authedUser);

    if (!documentsCreated) {
      // Cloud Function failed or timed out
      setError(t('login.errors.setupTimeout'));
      await auth.signOut();
      return;
    }
    await authedUser.getIdToken(true);

    // Verify claims were set
    const tokenResult = await authedUser.getIdTokenResult();
    if (!tokenResult.claims.client_key) {
      setError(t('login.errors.setupIncomplete'));
      await auth.signOut();
      return;
    }
    // Check if user needs onboarding
    const userDocPath = userPath(authedUser.uid);
    const userDoc = await getDoc(doc(db, userDocPath));
    const userData = userDoc.data();
    if (!userData?.has_completed_onboarding && !inviteCode) {
      setNewUser(authedUser);
      setShowOnboarding(true);
    } else {
      navigate(inviteCode ? `/join/${inviteCode}` : '/');
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    setIsAuthenticating(true); // Prevent redirect during auth flow

    try {
      const res = await signInWithPopup(auth, googleProvider);
      await completePostAuth(res.user);
    } catch (err: any) {
      setIsAuthenticating(false); // Allow redirect on error
      if (err.code === 'auth/popup-closed-by-user') {
        setError(t('login.errors.popupClosed'));
      } else {
        setError(t('login.errors.googleSignInFailed', { message: err.message }));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * School (district SSO) sign-in: home-realm discovery on the email, then
   * the district's configured provider. Unknown domains steer back to the
   * normal self-service flow — that's the expected answer, not an error.
   */
  const handleSchoolContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSchoolNotice(null);
    setIsAuthenticating(true);

    try {
      const resolution = await lookupSignInMethod(schoolEmail);
      if (resolution.method !== 'sso') {
        setEmail(schoolEmail);
        setSchoolMode(false);
        setSchoolNotice(t('login.school.notFound'));
        setIsAuthenticating(false);
        return;
      }

      await signInWithDistrict(auth, resolution);
      // Popup flows (Google districts) are signed in at this point. Redirect
      // flows (SAML/OIDC) have navigated away and come back through the
      // getRedirectResult effect below.
      if (auth.currentUser) {
        await completePostAuth(auth.currentUser);
      }
    } catch (err: any) {
      setIsAuthenticating(false);
      if (err instanceof SsoNotEnabledError) {
        setError(err.message);
      } else if (err?.code === 'auth/popup-closed-by-user') {
        setError(t('login.errors.popupClosed'));
      } else {
        setError(t('login.errors.schoolSignInFailed', { message: err?.message || err }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Finish SAML/OIDC school sign-ins when the identity provider redirects
  // the user back to this page.
  useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        if (result?.user) {
          setIsAuthenticating(true);
          await completePostAuth(result.user);
        }
      })
      .catch((err: any) => {
        setError(t('login.errors.schoolSignInFailed', { message: err?.message || err }));
      });
    // Run once on mount: redirect results are consumed on first read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setIsAuthenticating(true); // Prevent redirect during auth flow

    try {
      let credential;
      if (isSignUp) {
        // Managed school domains must not create password accounts. The
        // beforeCreate blocking function enforces this server-side; checking
        // here gives a helpful message instead of a rejected signup.
        const resolution = await lookupSignInMethod(email);
        if (resolution.method === 'sso' && !resolution.allow_password_fallback) {
          setError(
            t('login.errors.ssoRequired', {
              district: resolution.district_name || t('login.school.defaultName'),
            })
          );
          setSchoolMode(true);
          setSchoolEmail(email);
          setIsAuthenticating(false);
          setLoading(false);
          return;
        }

        credential = await createUserWithEmailAndPassword(auth, email, password);

        // Send verification email BEFORE signing out
        await sendEmailVerification(credential.user);
        // Store the email for display
        setPendingVerificationEmail(credential.user.email);

        // CRITICAL: Sign out immediately - user cannot use the app until verified
        // The Cloud Function will create the user docs, but with email_verified: false
        await auth.signOut();

        // Show verification message (not dialog, since user is signed out)
        setShowEmailVerification(true);
        setIsAuthenticating(false);
        setLoading(false);
        return; // Exit early - don't proceed with normal flow

      } else {
        // SIGN IN FLOW

        // Only check lockout if there's been a recent failed attempt (skip for clean logins)
        if (hasRecentFailedAttempt(email)) {
          try {
            const checkSignInAllowed = httpsCallable(functions, 'checkSignInAllowed');
            const lockoutCheck = await checkSignInAllowed({ email }) as {
              data: {
                allowed: boolean;
                reason?: string;
                requiresPasswordReset?: boolean;
                attemptCount?: number;
              }
            };

            if (!lockoutCheck.data.allowed) {
              setIsLockedOut(true);
              setLockoutMessage(lockoutCheck.data.reason || t('login.lockout.defaultMessage'));
              setRequiresPasswordReset(lockoutCheck.data.requiresPasswordReset || false);
              setLoading(false);
              setIsAuthenticating(false);
              return;
            }
          } catch (lockoutError) {        }
        }

        try {
          credential = await signInWithEmailAndPassword(auth, email, password);
        } catch (signInError: any) {
          // Record failed sign-in attempt for wrong password
          if (signInError.code === 'auth/wrong-password' || signInError.code === 'auth/invalid-credential') {
            // Track locally so we check lockout on next attempt
            recordLocalFailedAttempt(email);
            try {
              const recordFailedSignIn = httpsCallable(functions, 'recordFailedSignIn');
              const result = await recordFailedSignIn({ email }) as {
                data: {
                  allowed: boolean;
                  reason?: string;
                  requiresPasswordReset?: boolean;
                  attemptCount?: number;
                  attemptsRemaining?: number;
                }
              };

              if (!result.data.allowed) {
                setIsLockedOut(true);
                setLockoutMessage(result.data.reason || t('login.lockout.defaultMessage'));
                setRequiresPasswordReset(result.data.requiresPasswordReset || false);
                setLoading(false);
                setIsAuthenticating(false);
                return;
              } else if (result.data.attemptsRemaining !== undefined && result.data.attemptsRemaining <= 2) {
                setError(
                  t('login.errors.incorrectPasswordAttempts', { count: result.data.attemptsRemaining })
                );
                setLoading(false);
                setIsAuthenticating(false);
                return;
              }
            } catch (recordError) {            }
          }
          throw signInError; // Re-throw for normal error handling
        }

        // CRITICAL: Check if email is verified BEFORE allowing access
        // Reload user to get fresh emailVerified status
        await credential.user.reload();

        if (!credential.user.emailVerified) {
          // Email not verified - send another verification email and sign out
          await sendEmailVerification(credential.user);
          setPendingVerificationEmail(credential.user.email);
          await auth.signOut();
          setShowEmailVerification(true);
          setError(t('login.errors.emailNotVerified'));
          setIsAuthenticating(false);
          setLoading(false);
          return;
        }

        // Email is verified - proceed with normal flow
        // Clear local failed attempts cache
        clearLocalFailedAttempts(email);

        // Wait for Cloud Function to create user documents (for existing users migrating)
        await createUserDocIfMissing(credential.user);

        // Finalize sign-in: clear failed attempts + confirm email verification in one call
        try {
          const finalizeSignInFn = httpsCallable(functions, 'finalizeSignIn');
          await finalizeSignInFn({});
        } catch (err) {        }

        // Force token refresh to get custom claims
        await credential.user.getIdToken(true);

        // Verify claims were set
        const signinTokenResult = await credential.user.getIdTokenResult();
        if (!signinTokenResult.claims.client_key) {
          setError(t('login.errors.setupIncomplete'));
          await auth.signOut();
          return;
        }

        // Check if user needs onboarding
        const userDoc = await getDoc(doc(db, userPath(credential.user.uid)));
        const userData = userDoc.data();

        if (!userData?.has_completed_onboarding && !inviteCode) {
          setNewUser(credential.user);
          setShowOnboarding(true);
        } else {
          navigate(inviteCode ? `/join/${inviteCode}` : '/');
        }
      }
    } catch (err) {
      setIsAuthenticating(false); // Allow redirect on error
      const error = err as { code?: string; message?: string };
      if (error.code === 'auth/weak-password') {
        setError(t('login.errors.weakPassword'));
      } else if (error.code === 'auth/email-already-in-use') {
        setError(t('login.errors.emailAlreadyInUse'));
      } else if (error.code === 'auth/invalid-email') {
        setError(t('login.errors.invalidEmail'));
      } else if (error.code === 'auth/user-not-found') {
        setError(t('login.errors.userNotFound'));
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setError(t('login.errors.wrongPassword'));
      } else {
        setError(error.message || t('login.errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordLoading(true);

    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setForgotPasswordSent(true);
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        // Don't reveal if email exists for security
        setForgotPasswordSent(true);
      } else {
        setError(err.message || t('login.errors.resetEmailFailed'));
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    navigate(inviteCode ? `/join/${inviteCode}` : '/');
  };

  if (userLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography>{t('common:states.loading')}</Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', position: 'relative', bgcolor: 'background.default' }}>
        {/* Logo in top left corner */}
        <Box
          component="img"
          {...({ src: athletoLogo, alt: "Athleto" } as any)}
          onClick={() => navigate('/')}
          sx={{
            position: 'fixed',
            top: 24,
            left: 24,
            height: 48,
            width: 'auto',
            cursor: 'pointer',
            transition: 'transform 0.3s ease',
            zIndex: 10,
            '&:hover': {
              transform: 'scale(1.1)',
            },
          }}
        />

        <Container maxWidth="sm">
          <Card
            elevation={2}
            sx={{
              background: 'background.paper',
              p: 3,
            }}
          >
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Stack spacing={4}>
                <Typography variant="h3" fontWeight={800}>
                  {isSignUp ? t('login.title.signUp') : t('login.title.signIn')}
                </Typography>

            {inviteCode && (
              <Alert severity="info">
                {t('login.inviteNotice')}
              </Alert>
            )}


            {error && <Alert severity="error">{error}</Alert>}
            {schoolNotice && <Alert severity="info">{schoolNotice}</Alert>}

            {!schoolMode && (
              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />
                }
                onClick={handleGoogleSignIn}
                sx={{
                  color: colors.primary.contrastText,
                }}
              >
                {t('login.google.signInButton')}
              </Button>
            )}

            {schoolMode ? (
              <Box component="form" onSubmit={handleSchoolContinue}>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('login.school.instructions')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('login.school.cleverHint')}
                  </Typography>
                  <TextField
                    label={t('login.school.emailLabel')}
                    type="email"
                    fullWidth
                    required
                    autoFocus
                    value={schoolEmail}
                    onChange={(e) => setSchoolEmail(e.target.value)}
                    disabled={loading}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || !schoolEmail}
                    startIcon={
                      loading ? <CircularProgress size={20} color="inherit" /> : <SchoolIcon />
                    }
                    sx={{ color: colors.primary.contrastText }}
                  >
                    {t('common:actions.continue')}
                  </Button>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => {
                      setSchoolMode(false);
                      setError(null);
                      setSchoolNotice(null);
                    }}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                  >
                    {t('login.school.backToSignIn')}
                  </Link>
                </Stack>
              </Box>
            ) : (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                disabled={loading}
                startIcon={<SchoolIcon />}
                onClick={() => {
                  setSchoolMode(true);
                  setError(null);
                  setSchoolNotice(null);
                }}
              >
                {t('login.school.continueButton')}
              </Button>
            )}

            {!schoolMode && <Divider>{t('login.divider')}</Divider>}

            {!schoolMode && (
            <Box component="form" onSubmit={handleEmailAuth}>
              <Stack spacing={2}>
                <TextField
                  label={t('login.form.emailLabel')}
                  type="email"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <TextField
                  label={t('login.form.passwordLabel')}
                  type="password"
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                {!isSignUp && (
                  <Box sx={{ textAlign: 'right', mt: -1 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setForgotPasswordEmail(email);
                        setForgotPasswordSent(false);
                        setError(null);
                      }}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                      {t('login.form.forgotPasswordLink')}
                    </Link>
                  </Box>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading || !email || !password}
                  sx={{
                    color: colors.primary.contrastText,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    isSignUp ? t('login.actions.signUp') : t('login.actions.signIn')
                  )}
                </Button>
              </Stack>
            </Box>
            )}

            {!schoolMode && (
            <Typography variant="body2" color="text.secondary">
              {isSignUp ? t('login.toggle.haveAccount') : t('login.toggle.noAccount')}{' '}
              <Link
                {...({ component: "button", onClick: () => {
                  setIsSignUp(!isSignUp);
                  setError(null);
                } } as any)}
                sx={{
                  cursor: 'pointer',
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              >
                {isSignUp ? t('login.actions.signIn') : t('login.actions.signUp')}
              </Link>
            </Typography>
            )}

            <Box sx={{ maxWidth: 240, mx: 'auto', width: '100%' }}>
              <LanguageSwitcher variant="field" size="small" fullWidth />
            </Box>

          </Stack>
        </Box>
        </Card>
      </Container>

      {/* Onboarding Dialog */}
      {newUser && (
        <OnboardingDialog
          open={showOnboarding}
          userId={newUser.uid}
          userEmail={newUser.email}
          onComplete={handleOnboardingComplete}
        />
      )}

      {/* Email Verification Pending Message (shown when user is signed out after signup) */}
      {showEmailVerification && pendingVerificationEmail && !user && (
        <Dialog open onClose={() => { setShowEmailVerification(false); setPendingVerificationEmail(null); setError(null); }} maxWidth="xs" fullWidth>
          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: colors.primary.alpha[10],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 40, color: colors.primary.main }} />
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {t('login.verifyEmail.title')}
              </Typography>
              <Typography color="text.secondary">
                {t('login.verifyEmail.sentTo')}
              </Typography>
              <Typography fontWeight={600} color="primary">
                {pendingVerificationEmail}
              </Typography>
              <Alert severity="info" sx={{ width: '100%' }}>
                {t('login.verifyEmail.instructions')}
              </Alert>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setShowEmailVerification(false);
                  setPendingVerificationEmail(null);
                  setError(null);
                }}
                sx={{ color: colors.primary.contrastText }}
              >
                {t('login.actions.backToSignIn')}
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <Dialog open onClose={() => { if (!forgotPasswordLoading) { setShowForgotPassword(false); setForgotPasswordSent(false); } }} maxWidth="xs" fullWidth>
          <DialogContent sx={{ p: 3 }}>
            {!forgotPasswordSent ? (
              <Stack spacing={3}>
                <Typography variant="h5" fontWeight={700} textAlign="center">
                  {t('login.forgotPassword.label')}
                </Typography>
                <Typography color="text.secondary" textAlign="center">
                  {t('login.forgotPassword.instructions')}
                </Typography>
                <Box component="form" onSubmit={handleForgotPassword}>
                  <Stack spacing={2}>
                    <TextField
                      label={t('login.form.emailLabel')}
                      type="email"
                      fullWidth
                      required
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      disabled={forgotPasswordLoading}
                      autoFocus
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={forgotPasswordLoading || !forgotPasswordEmail}
                      sx={{ color: colors.primary.contrastText }}
                    >
                      {forgotPasswordLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        t('login.forgotPassword.sendButton')
                      )}
                    </Button>
                    <Button
                      variant="text"
                      fullWidth
                      onClick={() => setShowForgotPassword(false)}
                    >
                      {t('login.actions.backToSignIn')}
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={3} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: colors.primary.alpha[10],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 40, color: colors.primary.main }} />
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {t('login.forgotPassword.checkEmailTitle')}
                </Typography>
                <Typography color="text.secondary">
                  {/* <Trans>, not two half-sentences: the address does not
                      sit in the same place in every language. */}
                  <Trans
                    t={t}
                    i18nKey="login.forgotPassword.sent"
                    values={{ email: forgotPasswordEmail }}
                    components={{ email: <strong /> }}
                  />
                </Typography>
                <Alert severity="info" sx={{ width: '100%' }}>
                  {t('login.forgotPassword.checkSpam')}
                </Alert>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordSent(false);
                  }}
                  sx={{ color: colors.primary.contrastText }}
                >
                  {t('login.actions.backToSignIn')}
                </Button>
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Lockout Modal */}
      {isLockedOut && (
        <Dialog open maxWidth="xs" fullWidth>
          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'error.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ fontSize: 40 }}>🔒</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {t('login.lockout.title')}
              </Typography>
              <Typography color="text.secondary">
                {lockoutMessage}
              </Typography>
              {requiresPasswordReset ? (
                <>
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    {t('login.lockout.mustReset')}
                  </Alert>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      setIsLockedOut(false);
                      setShowForgotPassword(true);
                      setForgotPasswordEmail(email);
                      setForgotPasswordSent(false);
                    }}
                    sx={{ color: colors.primary.contrastText }}
                  >
                    {t('login.forgotPassword.label')}
                  </Button>
                </>
              ) : (
                <>
                  <Alert severity="info" sx={{ width: '100%' }}>
                    {t('login.lockout.waitOrReset')}
                  </Alert>
                  <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setIsLockedOut(false)}
                    >
                      {t('login.lockout.tryAgainLater')}
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        setIsLockedOut(false);
                        setShowForgotPassword(true);
                        setForgotPasswordEmail(email);
                        setForgotPasswordSent(false);
                      }}
                      sx={{ color: colors.primary.contrastText }}
                    >
                      {t('login.forgotPassword.label')}
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          </DialogContent>
        </Dialog>
      )}
      </Box>
    </ThemeProvider>
  );
}

export default Login;
